import {
  AcademicCapIcon,
  ArrowRightOnRectangleIcon,
  ArrowTrendingUpIcon,
  BookmarkIcon,
  BoltIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  GiftIcon,
  HomeIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  NewspaperIcon,
  PencilSquareIcon,
  PresentationChartLineIcon,
  RocketLaunchIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
  SparklesIcon,
  StarIcon,
  TableCellsIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react-v2/24/outline';

import {shouldHideCoaching, hideAIChatFeatures, shouldHideJobReferrals} from '../util';
import {getBrandName} from '../components/Brand';
import {DiscordIcon} from '../components/svg';

/**
 * SidebarMenu is the source of truth for the logged-in sidebar navigation
 * (Sidebar, SidebarNav and the mobile nav drawer). The logged-out top header
 * still reads from `constants/menu.js`, which lets us tune the two audiences
 * independently: this file leads with the things a member does every day
 * (dashboard, practice, their courses) and pushes acquisition-oriented
 * marketing pages into the More flyout.
 *
 * `sections` is an ordered array of groups rendered top to bottom. Each
 * section has the following fields available:
 *
 *   title - Optional uppercase label rendered above the group. Hidden while
 *           the sidebar is collapsed to a rail.
 *   items - Array of nav items (see below).
 *   shouldHide - Optional function taking appContext, returns a boolean. The
 *                whole section is dropped when it returns true.
 *
 * Nav items have the following fields available:
 *
 *   title - Text to be displayed.
 *   icon - Uninstantiated icon component, rendered at 20x20 in the rail.
 *   link - Relative URL, or a function taking appContext and returning one.
 *   activePaths - Array of next/router pathnames. Highlights when matched.
 *   activePrefixes - Array of pathname prefixes. Highlights when the current
 *                    pathname starts with one of them. Useful for whole
 *                    sub-trees like /courses/... where enumerating every
 *                    dynamic route is noisy. When more than one item matches,
 *                    the longest (most specific) prefix wins, so a sub-tree
 *                    item like Coach Dashboard's /coaching/dashboard doesn't
 *                    need to be excluded from the broader /coaching item.
 *   badge - Optional short text rendered in a pill after the title.
 *   forceAnchor - Use a native anchor element instead of react-router <Link>.
 *   shouldHide - Function implementing custom display logic. Takes appContext
 *                and returns a boolean.
 *   items - Optional array of child items, revealed in a full-height panel
 *           that slides out from behind the sidebar. Children accept
 *           title, description, icon, link, badge and forceAnchor.
 *   flyoutTitle - Optional uppercase label above the flyout's items.
 *   flyoutWidth - Optional tailwind width class, overriding the panel default.
 *   flyoutCloseDelay - Optional milliseconds to wait before closing this
 *                      flyout when the pointer crosses a plain row.
 *   cta - Optional {title, link} rendered as a footer inside the flyout.
 */
const SidebarMenu = {
  sections: [
    {
      items: [
        {
          title: 'Dashboard',
          icon: HomeIcon,
          // /home is the logged-in dashboard; / only redirects on a full load.
          link: '/home?src=nav',
          activePaths: ['/home', '/'],
        },
        {
          title: 'Coach Dashboard',
          icon: Squares2X2Icon,
          link: '/coaching/dashboard?src=nav',
          activePrefixes: ['/coaching/dashboard', '/coaching/submit'],
          shouldHide: ({coachSlug}) => !coachSlug,
        },
        {
          // Org (university) admins get their analytics up top, where the old
          // header's "View analytics" action lived.
          title: 'Analytics',
          icon: ChartBarIcon,
          link: '/analytics?src=nav',
          activePrefixes: ['/analytics'],
          shouldHide: ({currentUser}) => !currentUser?.isOrgAdmin,
        },
        {
          title: 'Courses',
          icon: BookOpenIcon,
          link: '/courses?src=nav',
          activePrefixes: ['/courses'],
          flyoutTitle: 'Interview courses',
          items: [
            {
              icon: ClipboardDocumentListIcon,
              title: 'Product Management',
              link: '/courses/pm?src=nav',
              badge: 'New',
            },
            {
              icon: UserGroupIcon,
              title: 'Engineering Management',
              link: '/courses/engineering-management?src=nav',
            },
            {
              icon: CodeBracketIcon,
              title: 'Software Engineering',
              link: '/courses/software-engineering?src=nav',
            },
            {
              icon: RocketLaunchIcon,
              title: 'Forward Deployed',
              link: '/courses/forward-deployed-engineering?src=nav',
              badge: 'Beta',
            },
            {
              icon: BoltIcon,
              title: 'AI Engineering',
              link: '/courses/ai-engineering?src=nav',
            },
            {
              icon: PuzzlePieceIcon,
              title: 'System Design',
              link: '/courses/system-design-interviews?src=nav',
            },
            {
              icon: PresentationChartLineIcon,
              title: 'Data Science',
              link: '/courses/data-science?src=nav',
            },
            {
              icon: CpuChipIcon,
              title: 'Machine Learning',
              link: '/courses/ml-engineer?src=nav',
            },
            {
              icon: TableCellsIcon,
              title: 'Data Engineering',
              link: '/courses/data-engineering?src=nav',
            },
            {
              icon: CurrencyDollarIcon,
              title: 'Investment Banking',
              link: '/courses/investment-banking?src=nav',
              badge: 'New',
            },
            {
              icon: SparklesIcon,
              title: 'Generative AI',
              link: '/courses/gen-ai-interviews?src=nav',
            },
            {
              icon: ArrowTrendingUpIcon,
              title: 'Data Analytics',
              link: '/courses/data-analytics?src=nav',
            },
            {
              icon: PuzzlePieceIcon,
              title: 'Security Engineer',
              link: '/courses/security-engineer?src=nav',
            },
            {
              icon: ChatBubbleLeftRightIcon,
              title: 'Eng Behavioral',
              link: '/courses/eng-behavioral?src=nav',
            },
            {
              icon: ClipboardDocumentListIcon,
              title: 'TPM',
              link: '/courses/tpm?src=nav',
            },
            {
              icon: BuildingOffice2Icon,
              title: 'Solutions Architect',
              link: '/courses/solution-architect-interview?src=nav',
            },
            {
              icon: PencilSquareIcon,
              title: 'UX / Product Design',
              link: '/courses/product-designer-interview?src=nav',
            },
            {
              icon: MegaphoneIcon,
              title: 'Product Marketing',
              link: '/courses/pmm?src=nav',
            },
            {
              icon: PresentationChartLineIcon,
              title: 'BizOps & Strategy',
              link: '/courses/bizops?src=nav',
            },
            {
              icon: TableCellsIcon,
              title: 'SQL Interviews',
              link: '/courses/sql-interviews?src=nav',
            },
          ],
          cta: {
            title: 'View all courses',
            link: '/courses?src=nav',
          },
        },
        {
          title: 'Questions',
          icon: CircleStackIcon,
          link: '/questions?src=nav',
          activePrefixes: ['/questions'],
          flyoutTitle: 'Top questions',
          items: [
            {
              icon: ClipboardDocumentListIcon,
              title: 'Product Management',
              link: '/questions?role=pm&src=nav',
            },
            {
              icon: CodeBracketIcon,
              title: 'Coding',
              link: '/questions?type=algorithms&filterBy=codeEditor&src=nav',
            },
            {
              icon: PuzzlePieceIcon,
              title: 'System Design',
              link: '/questions?type=system-design&src=nav',
            },
            {
              icon: TableCellsIcon,
              title: 'SQL',
              link: '/questions?type=sql&filterBy=codeEditor&src=nav',
            },
            {
              icon: RocketLaunchIcon,
              title: 'Forward Deployed',
              link: '/questions?role=forward-deployed-engineer&src=nav',
            },
            {
              icon: BoltIcon,
              title: 'AI Engineer',
              link: '/questions?role=ai-engineer&src=nav',
            },
            {
              icon: UserGroupIcon,
              title: 'Engineering Management',
              link: '/questions?role=em&src=nav',
            },
            {
              icon: ClipboardDocumentListIcon,
              title: 'TPM',
              link: '/questions?role=tpm&src=nav',
            },
            {
              icon: CpuChipIcon,
              title: 'Machine Learning',
              link: '/questions?role=ml-engineer&src=nav',
            },
            {
              icon: TableCellsIcon,
              title: 'Data Engineering',
              link: '/questions?role=data-engineer&src=nav',
            },
            {
              icon: PresentationChartLineIcon,
              title: 'Data Science',
              link: '/questions?role=data-science&src=nav',
            },
            {
              icon: ChartBarIcon,
              title: 'Data Analytics',
              link: '/questions?role=data-analyst&src=nav',
            },
            {
              icon: CurrencyDollarIcon,
              title: 'Investment Banking',
              link: '/questions?role=investment-banking&src=nav',
            },
          ],
          cta: {
            title: 'Browse all questions',
            link: '/questions?src=nav',
          },
        },
        {
          title: 'Practice',
          icon: ChatBubbleLeftRightIcon,
          link: '/practice?src=nav',
          activePrefixes: ['/practice'],
        },
        {
          title: 'Coaching',
          icon: UserIcon,
          link: '/coaching?src=nav',
          activePrefixes: ['/coaching', '/coach', '/resume'],
          flyoutTitle: 'Coaching & feedback',
          items: [
            {
              icon: ClipboardDocumentCheckIcon,
              title: 'Mock Interviews & Coaching',
              description: 'Practice with our team of senior tech coaches.',
              link: '/coaching?category=mock_interviews&src=nav',
            },
            {
              icon: DocumentTextIcon,
              title: 'Resume Review',
              description: 'Get your resume reviewed by a senior tech recruiter.',
              link: '/resume?src=nav',
            },
            {
              icon: ChatBubbleLeftRightIcon,
              title: 'Salary Negotiation',
              description: 'Increase your offer with our expert negotiators.',
              link: '/coaching?category=negotiation&src=nav',
            },
          ],
          shouldHide: ({currentUser}) => shouldHideCoaching(currentUser),
        },
        {
          title: 'Company Guides',
          icon: BuildingOffice2Icon,
          link: '/guides?src=nav',
          activePrefixes: ['/guides'],
        },
        {
          title: 'Experiences',
          icon: DocumentMagnifyingGlassIcon,
          link: '/experiences?src=nav',
          activePrefixes: ['/experiences'],
        },
        {
          title: 'Resumes',
          icon: DocumentTextIcon,
          link: '/resumes?src=nav',
          activePrefixes: ['/resumes'],
        },
        {
          title: 'AI Chat',
          icon: SparklesIcon,
          link: '/chat?src=nav',
          activePrefixes: ['/chat'],
          shouldHide: ({currentUser}) => hideAIChatFeatures(currentUser),
        },
        {
          title: 'Job Referrals',
          icon: BriefcaseIcon,
          link: '/jobs/referrals?src=nav',
          activePrefixes: ['/jobs/referrals'],
          shouldHide: ({currentUser}) => shouldHideJobReferrals(currentUser),
        },
        {
          title: 'Resources',
          icon: NewspaperIcon,
          link: '/resources?src=nav',
          activePrefixes: ['/resources'],
          shouldHide: ({currentUser}) => !currentUser?.org,
        },
        {
          title: 'Community',
          icon: DiscordIcon,
          link: '/discord?src=nav',
          forceAnchor: true,
          shouldHide: ({hasSubscription}) => !hasSubscription,
        },
        {
          title: 'More',
          icon: EllipsisHorizontalIcon,
          flyoutTitle: 'More',
          flyoutCloseDelay: 450,
          activePaths: ['/perks'],
          items: [
            {
              icon: BriefcaseIcon,
              title: 'Job boards',
              description: 'Curated job listings and company hiring pages.',
              link: '/jobs?src=nav',
            },
            {
              icon: GiftIcon,
              title: 'Perks',
              description: 'Discounts and offers from our partners.',
              link: '/perks?src=nav',
            },
            {
              icon: PencilSquareIcon,
              title: 'Blog',
              description: 'Interviewing tips, strategies, and more.',
              link: '/blog?src=nav',
            },
            {
              icon: InformationCircleIcon,
              title: 'Work with us',
              description: `Help us grow the ${getBrandName()} community.`,
              link: '/collaborate?src=nav',
            },
            {
              icon: UserGroupIcon,
              title: 'Affiliate program',
              description: 'Recommend us to others and earn commission.',
              link: '/affiliate?src=nav',
            },
            {
              icon: AcademicCapIcon,
              title: 'For universities',
              description: 'Give your students tech interview prep.',
              link: '/pages/university?src=nav',
            },
            {
              icon: BriefcaseIcon,
              title: 'For businesses',
              description: 'Improve your placement rates and outcomes.',
              link: '/pages/business?src=nav',
            },
            {
              icon: MagnifyingGlassIcon,
              title: 'For recruiters',
              description: `Post a job on ${getBrandName()}'s exclusive job board.`,
              link: '/jobs/post?src=nav',
            },
          ],
        },
      ],
    },
  ],

  /**
   * Rendered inside the sidebar footer user menu and the top bar avatar menu.
   * Each item accepts title, link, icon, forceAnchor and shouldHide.
   */
  accountItems: [
    {title: 'Profile', icon: UserCircleIcon, link: '/account/profile?src=nav'},
    {
      title: 'Saved',
      icon: BookmarkIcon,
      link: ({currentUser}) => (currentUser
        ? `/users/${currentUser.uuid}/saved?src=nav`
        : '/login?src=nav'),
    },
    {title: 'Karma', icon: StarIcon, link: '/karma?src=nav'},
    {title: 'Refer friends', icon: GiftIcon, link: '/referrals?src=nav'},
    {title: 'Support', icon: LifebuoyIcon, link: '/support'},
    {title: 'Settings', icon: Cog6ToothIcon, link: '/account?src=nav'},
    {
      title: 'Log out', icon: ArrowRightOnRectangleIcon, link: '/logout?src=nav', forceAnchor: true,
    },
  ],
};

export default SidebarMenu;
